import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const ThreeDodecahedron = ({ position }) => {
  const canvasRef = useRef();
  const scene = useRef(null);
  const camera = useRef(null);
  const renderer = useRef(null);
  const dodecahedronMesh = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const container = canvas.parentElement;
    
    // Small delay to ensure container is properly rendered
    const initThree = () => {
      // Ensure container has dimensions
      const containerWidth = container.clientWidth || 400;
      const containerHeight = container.clientHeight || 300;
    
      scene.current = new THREE.Scene();
      camera.current = new THREE.PerspectiveCamera(75, containerWidth / containerHeight, 0.1, 1000);
      renderer.current = new THREE.WebGLRenderer({ canvas: canvas, alpha: true });
      renderer.current.setSize(containerWidth, containerHeight);
      renderer.current.setClearColor(0x000000, 0);

      const dodecahedronGeometry = new THREE.DodecahedronGeometry(3);
      const dodecahedronMaterial = new THREE.MeshBasicMaterial({ color: 0xdebd20, wireframe: true });
      dodecahedronMesh.current = new THREE.Mesh(dodecahedronGeometry, dodecahedronMaterial);
      scene.current.add(dodecahedronMesh.current);

      camera.current.position.z = 6;

      const animate = () => {
        requestAnimationFrame(animate);
        if (dodecahedronMesh.current) {
          dodecahedronMesh.current.rotation.x += 0.001;
          dodecahedronMesh.current.rotation.y += 0.001;
        }
        renderer.current.render(scene.current, camera.current);
      };

      animate();

      // Handle window resize
      const handleResize = () => {
        if (container && camera.current && renderer.current) {
          const containerWidth = container.clientWidth || 400;
          const containerHeight = container.clientHeight || 300;
          camera.current.aspect = containerWidth / containerHeight;
          camera.current.updateProjectionMatrix();
          renderer.current.setSize(containerWidth, containerHeight);
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        if (scene.current && dodecahedronMesh.current) {
          scene.current.remove(dodecahedronMesh.current);
        }
        if (dodecahedronGeometry) dodecahedronGeometry.dispose();
        if (dodecahedronMaterial) dodecahedronMaterial.dispose();
        if (renderer.current) renderer.current.dispose();
      };
    };

    // Initialize Three.js with a small delay
    const timeoutId = setTimeout(initThree, 100);

    return () => {
      clearTimeout(timeoutId);
      if (scene.current && dodecahedronMesh.current) {
        scene.current.remove(dodecahedronMesh.current);
      }
      if (renderer.current) {
        renderer.current.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (dodecahedronMesh.current) {
      dodecahedronMesh.current.position.set(
        position.x, 
        position.y * 0.1, // Scale the vertical movement to be more visible
        position.z
      );
    }
  }, [position]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />;
};

export default ThreeDodecahedron;
